import React, { useState, useRef, useEffect } from "react";
import { FaChevronDown } from "react-icons/fa";

// Material UI Components
import Button from "@mui/material/Button";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Divider from "@mui/material/Divider";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";

type MenuButtonOption = {
  divider?: boolean;
  label: string | React.ReactNode;
  value: string;
  danger?: boolean;
  icon?: React.ReactNode;
};

interface MenuButtonOptionProps {
  option: MenuButtonOption;
  onSelect: (option: any) => void;
  onRender?: (option: any, index: number) => React.ReactNode;
  index: number;
}

const MenuButtonOption = ({ option, onSelect, onRender, index }: MenuButtonOptionProps) => {
  return (
    <MenuItem onClick={() => onSelect(option)}>
      {onRender
        ? onRender(option, index)
        : (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {option.icon}

            <Typography
              variant="body2"
              color={option.danger ? 'error' : 'inherit'}
            >
              {option.label || option.value}
            </Typography>
          </Box>
        )
      }
    </MenuItem>
  );
};

// Types
interface MenuButtonProps {
  options: MenuButtonOption[];
  showArrow?: boolean;
  onSelect?: (option: any) => void;
  children: React.ReactNode;
  renderOption?: (option: any, index: number) => React.ReactNode;
  className?: string;
  variant?: 'text' | 'outlined' | 'contained';
  color?: 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';
  size?: 'small' | 'medium' | 'large';
}

const MenuButton: React.FC<MenuButtonProps> = ({
  options = [],
  showArrow = true,
  onSelect,
  children,
  renderOption,
  variant = 'outlined',
  color = 'primary',
  size = 'medium',
  ...props
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSelect = (option: any) => {
    if (onSelect) onSelect(option);
    handleClose();
  };

  return (
    <div>
      <Button
        variant={variant}
        color={color}
        size={size}
        onClick={handleClick}
        endIcon={showArrow ? <FaChevronDown /> : undefined}
        {...props}
      >
        {children}
      </Button>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        slotProps={{
          paper: {
            sx: {
              minWidth: 200,
            }
          }
        }}
      >
        {options.map((option, idx) => {
          if (option.divider) return <Divider key={idx} />;

          return (
            <MenuButtonOption
              key={option.value}
              option={option}
              onSelect={handleSelect}
              onRender={renderOption}
              index={idx}
            />
          );
        })}
      </Menu>
    </div>
  );
}

export default MenuButton;
